import {
  getCountryOptions,
  getStateLabel,
  getStateValues,
  usaStates as states,
} from '@stax-core/lateralus-toolkit';
import { RegistrationFieldPermissionsField } from '@stax-core/types-omni';
import { FunctionComponent, useContext, useEffect, useMemo } from 'react';
import { Field } from 'react-final-form';
import {
  Checkbox,
  DateInput,
  FieldError,
  Group,
  Input,
  ResponsiveMargin,
  Select,
  ToggleableField,
  ToggleableFieldProps,
} from '../../components';
import AddressAutocomplete from '../../components/AddressAutocomplete/AddressAutocomplete';
import { FieldPermissionFlag } from '../../components/FieldPermissionFlag';
import { SsnInput } from '../../components/SsnInput';
import { BrandStore, RegistrationStore } from '../../context';
import { handleAddressAutocompleteSelect } from '../../util/addressAutocomplete.util';
import { formatPhoneNumber } from '../../util/format.util';
import { get } from '../../util/object.util';
import { RepresentativeFieldsProps } from './FinancialFields.types';
import { getRepresentativeFieldNames } from './FinancialFields.util';

export const RepresentativeFields: FunctionComponent<
  RepresentativeFieldsProps
> = props => {
  const {
    index,
    mode,
    isMobile,
    form: { mutators },
  } = props;

  const {
    state: { brand },
  } = useContext(BrandStore);

  const {
    state: { registration: storedRegistration },
  } = useContext(RegistrationStore);

  const isSigner = isNaN(index!);
  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: mode === 'review',
    defaultMode: mode === 'review' ? 'display' : 'edit',
  };

  const fieldNames = useMemo(
    () => getRepresentativeFieldNames(isSigner, index),
    [isSigner, index],
  );

  const addressCountry = get(props.values, fieldNames.address_country);

  const isAddressStateVisible =
    ['USA', 'CAN'].includes(addressCountry) || !addressCountry;

  const stateLabel = getStateLabel(addressCountry);
  const stateValues = getStateValues(addressCountry);
  const countryOptions = getCountryOptions();

  // If user changes country to non-USA we need to clear the state field value
  useEffect(() => {
    if (addressCountry && !['USA', 'CAN'].includes(addressCountry)) {
      mutators.setFieldValue(fieldNames.address_state, '');
    }
  }, [mutators, addressCountry, fieldNames]);

  return (
    <>
      <Group direction="row" space="0.5rem/0px">
        <Group direction="column" style={{ flex: '70%' }}>
          <ToggleableField
            name={fieldNames.title}
            label="Title"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} autoFocus={isSigner} />}
          </ToggleableField>
          <FieldError name={fieldNames.title} />
        </Group>

        <Group direction="column" style={{ flex: '30%' }}>
          <ToggleableField
            name={fieldNames.ownership_percentage}
            label={isMobile ? 'Own. %' : 'Ownership Percentage'}
            {...defaultFieldProps}
          >
            {props => (
              <Input
                {...props.input}
                tooltip="All representatives with 25% or more ownership of the business must be added as representatives on the application."
                onChange={e => {
                  props.input.onChange(e);

                  if (isSigner) {
                    mutators.setFieldValue(
                      'has0PercentOwnership',
                      e.target.value === '0',
                    );
                  }
                }}
              />
            )}
          </ToggleableField>
          <FieldError name={fieldNames.ownership_percentage} />
        </Group>
      </Group>

      {!isSigner && (
        <Group direction="column">
          <ToggleableField
            name={fieldNames.type}
            label="Representative Type"
            {...defaultFieldProps}
          >
            {props => (
              <Select {...props.input}>
                <option value="" disabled />
                <option value="Beneficial Owner">Beneficial Owner</option>
                <option value="Guarantor">Guarantor</option>
              </Select>
            )}
          </ToggleableField>
          <FieldError name={fieldNames.type} />
        </Group>
      )}

      <Group direction={isMobile ? 'column' : 'row'} space="0.5rem/0px">
        <Group direction="column" style={{ flex: '50%' }}>
          <ToggleableField
            name={fieldNames.first_name}
            label="First Name"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name={fieldNames.first_name} />
        </Group>
        <Group direction="column" style={{ flex: '50%' }}>
          <ToggleableField
            name={fieldNames.last_name}
            label="Last Name"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name={fieldNames.last_name} />
        </Group>
      </Group>

      <Group direction="column">
        <ToggleableField
          name={fieldNames.email}
          label="Contact Email"
          {...defaultFieldProps}
        >
          {props => <Input {...props.input} type="email" />}
        </ToggleableField>
        <FieldError name={fieldNames.email} />
      </Group>

      <Group direction="column">
        <ToggleableField
          name={fieldNames.phone}
          label="Phone Number"
          parse={formatPhoneNumber}
          format={formatPhoneNumber}
          {...defaultFieldProps}
        >
          {props => <Input {...props.input} type="tel" maxLength={14} />}
        </ToggleableField>
        <FieldError name={fieldNames.phone} />
      </Group>

      <Group direction="column">
        <ToggleableField
          name={fieldNames.date_of_birth}
          label="Date of Birth"
          {...defaultFieldProps}
        >
          {props => <DateInput {...props.input} />}
        </ToggleableField>
        <FieldError name={fieldNames.date_of_birth} />
      </Group>

      <Group direction="column">
        <ToggleableField
          name={fieldNames.citizenship}
          label="Citizenship"
          {...defaultFieldProps}
          type="select"
          options={countryOptions}
          optionLabelKey="label"
          optionValueKey="value"
        >
          {props => (
            <Select {...props.input}>
              <option value="" disabled />
              {countryOptions.map((country, index) => (
                <option key={index} value={country.value}>
                  {country.label}
                </option>
              ))}
            </Select>
          )}
        </ToggleableField>
        <FieldError name={fieldNames.citizenship} />
      </Group>

      <Group direction="column">
        <ToggleableField
          name={fieldNames.ssn}
          label="Social Security Number"
          secure
          {...defaultFieldProps}
        >
          {props => (
            <SsnInput
              {...props.input}
              tooltip="Social Security Number / Social Insurance Number is required to satisfy the Patriot Act and FinCEN requirements."
            />
          )}
        </ToggleableField>
        <FieldError name={fieldNames.ssn} />
      </Group>

      <Group direction="column">
        <FieldPermissionFlag
          fields={
            [
              fieldNames.address_city,
              fieldNames.address_state,
              fieldNames.address_zip,
              fieldNames.address_country,
            ] as RegistrationFieldPermissionsField[]
          }
        >
          {({ atLeastOneNone }) => (
            <ToggleableField
              name={fieldNames.address_1}
              label="Home Address Line 1"
              {...defaultFieldProps}
            >
              {props => (
                <AddressAutocomplete
                  onSelect={data =>
                    handleAddressAutocompleteSelect(
                      data,
                      {
                        streetAddress: fieldNames.address_1,
                        city: fieldNames.address_city,
                        state: fieldNames.address_state,
                        zip: fieldNames.address_zip,
                        country: fieldNames.address_country,
                      },
                      mutators.setFieldValue,
                    )
                  }
                  inputProps={{ ...props.input }}
                  onChange={value =>
                    mutators.setFieldValue(fieldNames.address_1, value)
                  }
                  // IF address_city, address_state, address_zip, or address_country fields are not visible,
                  // THEN disable the autocomplete
                  // since we do not want to allow the user to attempt to change non-visible (possibly prefilled) data
                  disableAutocomplete={atLeastOneNone}
                />
              )}
            </ToggleableField>
          )}
        </FieldPermissionFlag>
        <FieldError name={fieldNames.address_1} />
      </Group>

      {/* if address_1 is not readable, also hide address_2 */}
      <FieldPermissionFlag field={fieldNames.address_1}>
        <Group direction="column">
          <ToggleableField
            name={fieldNames.address_2}
            label="Home Address Line 2 (optional)"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name={fieldNames.address_2} />
        </Group>
      </FieldPermissionFlag>

      <Group direction={isMobile ? 'column' : 'row'} space="0.5rem/0px">
        <Group direction="column" style={{ flex: '40%' }}>
          <ToggleableField
            name={fieldNames.address_city}
            label="City"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name={fieldNames.address_city} />
        </Group>

        {isAddressStateVisible && (
          <Group direction="column" style={{ flex: '30%' }}>
            <ToggleableField
              name={fieldNames.address_state}
              label={stateLabel}
              {...defaultFieldProps}
              type="select"
              options={stateValues}
              optionLabelKey="name"
              optionValueKey="abbr"
            >
              {props => (
                <Select {...props.input}>
                  <option value="" disabled />
                  {stateValues.map((state, index) => (
                    <option key={index} value={state.abbr}>
                      {state.name}
                    </option>
                  ))}
                </Select>
              )}
            </ToggleableField>
            <FieldError name={fieldNames.address_state} />
          </Group>
        )}

        <Group direction="column" style={{ flex: '30%' }}>
          <ToggleableField
            name={fieldNames.address_zip}
            label="Zip"
            {...defaultFieldProps}
          >
            {props => <Input {...props.input} />}
          </ToggleableField>
          <FieldError name={fieldNames.address_zip} />
        </Group>

        <Group direction="column" style={{ flex: '30%' }}>
          <ToggleableField
            name={fieldNames.address_country}
            label="Country"
            {...defaultFieldProps}
            type="select"
            options={countryOptions}
            optionLabelKey="label"
            optionValueKey="value"
          >
            {props => (
              <Select {...props.input}>
                <option value="" disabled />
                {countryOptions.map((country, index) => (
                  <option key={index} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </Select>
            )}
          </ToggleableField>
          <FieldError name={fieldNames.address_country} />
        </Group>
      </Group>

      {brand?.showIdentificationDetails && (
        <>
          <ResponsiveMargin
            desktop="0px"
            mobile="16px 0px 8px 0px"
            data-testid="identification-details"
          >
            <strong>Identification Details</strong>
          </ResponsiveMargin>

          <Group
            direction={isMobile ? 'column' : 'row'}
            style={{ marginTop: 8 }}
            space="0.5rem/0px"
          >
            <Group direction="column" style={{ flex: '50%' }}>
              <ToggleableField
                name={fieldNames.drivers_license_id}
                label="Drivers License ID"
                {...defaultFieldProps}
              >
                {props => <Input {...props.input} />}
              </ToggleableField>
              <FieldError name={fieldNames.drivers_license_id} />
            </Group>
            <Group direction="column" style={{ flex: '50%' }}>
              <ToggleableField
                name={fieldNames.drivers_license_state}
                label="Issuer State"
                {...defaultFieldProps}
                type="select"
                options={states}
                optionLabelKey="name"
                optionValueKey="abbr"
              >
                {props => (
                  <Select {...props.input}>
                    <option value="" disabled />
                    {states.map((state, index) => (
                      <option key={index} value={state.abbr}>
                        {state.name}
                      </option>
                    ))}
                  </Select>
                )}
              </ToggleableField>
              <FieldError name={fieldNames.drivers_license_state} />
            </Group>
          </Group>
        </>
      )}

      <Group direction="column">
        {isSigner && storedRegistration?.company_type !== 'S' && (
          <FieldPermissionFlag
            field="meta.ownership_percentage"
            // only render this checkbox if the ownership_percentage field is writeable
            renderIf="write"
          >
            <Field name="has0PercentOwnership" type="checkbox">
              {props => (
                <Checkbox
                  {...props.input}
                  id="has0PercentOwnership"
                  label="Check here if the principal signer has 0% ownership."
                  style={{ margin: '0.25rem' }}
                  onChange={e => {
                    props.input.onChange(e);
                    mutators.setFieldValue(
                      fieldNames.ownership_percentage,
                      e.target.checked ? '0' : '',
                    );
                  }}
                />
              )}
            </Field>
          </FieldPermissionFlag>
        )}
      </Group>
    </>
  );
};
